// Taşeron portalı — "aksayan analiz" şeridi (plan S19-D7, tasarım review §4/§8, AC6). Belgeler
// VE bir personelin belge paneli (D9) AYNI bileşeni KENDİ `scope`'uyla kullanır — D9 canlı
// (`personnel/PersonnelDocuments.tsx` `subject` ile `UploadModal` açıyor, `UploadModal.tsx`
// `subjectUserUuid`'i başlatma isteğine taşıyor), yani kişi-pinlenmiş aksayan analizler de
// vardır ve KENDİ SAYFASINDA kurtarılmalıdır — Belgeler bunları GÖRMEMELİDİR (yanlış kapsamda
// dosyalanma riski, bkz. `StrandedLinkModal`'ın `target` notu).
//
// ADAY kümesi: `scope` eşleşir (taşeron → `subjectUserUuid === null`; kişi →
// `subjectUserUuid === scope.userUuid`) VE (`status === 'Failed'` YA DA `status ===
// 'Succeeded'` VE hem `acceptedItemCount` hem `pendingItemCount` `0` — DTO değişmezi:
// ikisinin toplamı `items.length`'e eşittir, RC-1 V-12'nin "sıfır kalemli Succeeded"
// durumu budur). Bu ikisi `?status=awaiting-review` VE `?status=active`'in İKİSİNDEN de
// dışlanır (§20.3) — tek kurtarma yolu `?status=all`, o yüzden burada İSTEMCİ tarafında
// süzülür (her iki kullanım AYNI `queryKey`'i paylaşır, tek istek).
//
// "HÂLÂ aksıyor mu" TÜRETİLİR, HATIRLANMAZ. Analiz satırının kendisinde "elle bağlandı"
// bayrağı YOKTUR (kalem sayaçları `items[]` üzerinden tanımlıdır, kaynak dokümanlar üzerinden
// DEĞİL — bağlama analiz satırını DEĞİŞTİRMEZ) — bu yüzden bir aday, kendi §20.1
// `sources[].documentUuid`'lerinden EN AZ BİRİ çağıranın KENDİ listesinde (Belgeler'in
// taşeron listesi / panelin kişi listesi) YOKSA hâlâ aksıyor sayılır. Kullanıcı
// `StrandedLinkModal` ile bağladığı ANDA o kaynak listeye girer ve bu şerit kendiliğinden
// söner — istemci belleği YOK.
//
// **Bulgu (`PendingAnalysesStrip` ile AYNI aile — owner raporu sonrası tarandı): bu şerit de
// toplu tek satır + tek CTA hedefi taşıyordu.** `fileCount` TÜM adaylar üzerinden toplanıp
// gösteriliyor, CTA ise yalnız `stranded[0]`'a (`target`) açılıyordu — 2. ve sonraki aksayan
// analizin dosyaları modal üzerinden HİÇBİR yoldan bağlanamıyordu. Düzeltme AYNI desen: her
// aksayan analiz KENDİ satırını alır, KENDİ `pendingCount`'una ve KENDİ `StrandedLinkModal`
// çağrısına bağlanır. Satırlar ÇAPRAZLANMAZ/`+N daha` ile GİZLENMEZ.
//
// **Odak dönüşü satır başına DEĞİŞİR** — `useReturnFocus.ref` (TEK sabit tetikleyici) burada
// YANLIŞ olurdu: aynı `ref` nesnesi her satırın `Button`'ına verilirse DOM'a EN SON monte olan
// kazanır, ondan önceki satırların tıklaması yanlış düğmeye odak döndürür/hiçbirine dönmez.
// Doğru araç zaten `useReturnFocus.capture` — dosyanın kendi yorumunda "satır başına DEĞİŞEN
// tetikleyici" için tasarlanmış, `DocumentsPage.tsx`/`personnel/PersonnelDocuments.tsx`'in sil
// düğmesiyle AYNI çağrı biçimi (`capture(e.currentTarget)` tıklama ANINDA).

import {
  type ApiError,
  Button,
  Card,
  ConfirmDialog,
  type DocumentAnalysisDetailDto,
  type DocumentAnalysisDto,
  currentCulture,
  documentAnalysesApi,
} from '@isgportal/shared';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { Trash2, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../lib/format';
import { supplierContractorKeys } from '../lib/supplierContractorKeys';
import { useDocumentAnalysisList } from '../lib/useDocumentAnalysis';
import { useReturnFocus } from '../lib/useReturnFocus';
import { StrandedLinkModal, type StrandedTarget } from './StrandedLinkModal';

/** Analizin kapsamı (taşeron → `subjectUserUuid === null`; kişi → o kişinin uuid'i). `PendingAnalysesStrip`
 * de aynı kuralla süzer (GL5 — kişi çekmecesinin bekleyen onay şeridi). */
export function scopeMatches(item: DocumentAnalysisDto, scope: StrandedTarget): boolean {
  return scope.kind === 'personnel'
    ? item.subjectUserUuid === scope.userUuid
    : item.subjectUserUuid === null;
}

function isStrandedCandidate(item: DocumentAnalysisDto, scope: StrandedTarget): boolean {
  if (!scopeMatches(item, scope)) {
    return false;
  }
  if (item.status === 'Failed') {
    return true;
  }
  return item.status === 'Succeeded' && item.acceptedItemCount === 0 && item.pendingItemCount === 0;
}

export function StrandedAnalysesStrip({
  companyId,
  scope,
  linkedDocumentUuids,
}: {
  companyId: number | null;
  scope: StrandedTarget;
  linkedDocumentUuids: Set<string>;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { items } = useDocumentAnalysisList('all');
  const culture = currentCulture();
  const [linking, setLinking] = useState<DocumentAnalysisDto | null>(null);
  // Modal kapanınca odağın DÖNECEĞİ tetikleyici (QA D12 — D2'nin bu yüzeye ulaşmamış hâli).
  // Bkz. `useReturnFocus`: `Modal` bir `Dialog.Trigger` taşımadığı için Radix'in kendi geri
  // yüklemesi HİÇBİR modalda çalışmaz, odak `<body>`'ye düşer. Satır sayısı BİRDEN FAZLA
  // olduğundan burada `.ref` (sabit tek tetikleyici) DEĞİL, `.capture` (satır başına DEĞİŞEN
  // tetikleyici, tıklama ANINDA yazılır) kullanılır — dosya üstündeki not.
  const linkTrigger = useReturnFocus();

  function closeLinking() {
    setLinking(null);
    linkTrigger.restore();
  }

  // Aksayan bir analizin TAMAMINDAN vazgeçme (§20.4) — `DocumentAnalysisResultPage.tsx`'in
  // `Analizi sil` akışıyla AYNI `ConfirmDialog` kablolaması ve AYNI kopya anahtarları
  // (`analysis.discard.*`), tek fark burada N satır olabildiği için tetikleyici de
  // `linkTrigger` gibi `.capture` kullanır. Bu şerit HİÇBİR sil eylemi TAŞIMIYORDU — owner
  // raporu: kalıcı/aksayan analizler tek başına yığılıyor, hiçbir yoldan kapatılamıyordu.
  const [discarding, setDiscarding] = useState<DocumentAnalysisDto | null>(null);
  const discardTrigger = useReturnFocus();

  function closeDiscarding() {
    setDiscarding(null);
    discardTrigger.restore();
  }

  const discardMutation = useMutation<
    Awaited<ReturnType<typeof documentAnalysesApi.discardDocumentAnalysis>>,
    ApiError,
    string
  >({
    mutationFn: (analysisUuid) => documentAnalysesApi.discardDocumentAnalysis(analysisUuid),
    onSuccess: () => {
      // AYNI üç anahtar — `DocumentAnalysisResultPage.tsx`'in kendi discard'ının geçersiz
      // kıldığı ile BİREBİR (bu şerit de o sorgulardan türer, ikinci bir anahtar İCAT EDİLMEZ).
      queryClient.invalidateQueries({
        queryKey: supplierContractorKeys.analyses(companyId, 'awaiting-review'),
      });
      queryClient.invalidateQueries({
        queryKey: supplierContractorKeys.analyses(companyId, 'active'),
      });
      queryClient.invalidateQueries({
        queryKey: supplierContractorKeys.analyses(companyId, 'all'),
      });
      closeDiscarding();
    },
  });

  const candidates = items.filter((item) => isStrandedCandidate(item, scope));

  // Aday kümesi normalde boş → `queries: []` → `useQueries` SIFIR ağ isteği atar. Var olan
  // sorgu katmanıyla (mevcut `queryKey` — tepsi/sonuç sayfası AYNI anahtarı zaten kullanıyor
  // olabilir, o zaman istek TEKRARLANMAZ, önbellekten gelir) önbelleklenir/tekilleşir.
  const detailQueries = useQueries({
    queries: candidates.map((item) => ({
      queryKey: supplierContractorKeys.analysis(companyId, item.uuid),
      queryFn: () => documentAnalysesApi.getDocumentAnalysis(item.uuid),
      enabled: companyId != null,
    })),
  });

  const stranded = candidates
    .map((item, index) => {
      const detail = detailQueries[index]?.data as DocumentAnalysisDetailDto | undefined;
      if (!detail) {
        // Kaynak listesi henüz gelmedi — türetilecek bir şey yok; ADAY olduğu için iyimser
        // göster (önceki, sorgu-öncesi davranışla aynı) — yanlış negatif üretmez.
        return { item, pendingCount: item.sourceCount };
      }
      const pendingSources = detail.sources.filter(
        (source) => !linkedDocumentUuids.has(source.documentUuid),
      );
      return { item, pendingCount: pendingSources.length };
    })
    .filter((entry) => entry.pendingCount > 0);

  if (stranded.length === 0) {
    return null;
  }

  return (
    <>
      {/* Sol kenar sarı bandı: bu şerit sayfada bekleyen-onay şeridiyle YAN YANA durur ve
          ikisi bugün birbirinin AYNISI görünüyordu. Bant, uyarı token'ını kendi tasarlandığı
          biçimde (ZEMİN olarak) kullanır — glif rengi olarak değil. */}
      <Card className="border-border-strong border-l-2 border-l-warning">
        <ul className="flex flex-col divide-y divide-border">
          {stranded.map((entry) => (
            <li
              key={entry.item.uuid}
              className="flex flex-wrap items-center justify-between gap-3 p-4"
            >
              <div className="flex items-start gap-3">
                {/* `text-warning-foreground` = grafit; koyu temada grafit yüzey üstünde 1.00:1,
                    yani glif GÖRÜNMEZ. Tek başına duran uyarı glifi `text-fg` ile çizilir. */}
                <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-fg" aria-hidden="true" />
                <div>
                  <p className="text-sm font-medium text-fg">
                    {t('documents.stranded.title', { count: entry.pendingCount })}
                  </p>
                  {/* Aynı zaman etiketi `PendingAnalysesStrip` ile — iki şerit kardeş okunsun
                      diye AYNI anahtar, ikinci bir "analiz zamanı" anahtarı MİNTLENMEDİ. */}
                  <p className="mt-0.5 text-xs text-muted">
                    {t('documents.pending.queuedAt', {
                      date: formatDateTime(entry.item.queuedAt, culture),
                    })}
                  </p>
                </div>
              </div>
              {/* `Bilgilerini elle gir` birincil kurtarma yolu KALIR — `Analizi sil` ikincil
                  (`ghost`) ağırlıkta, aynı satırda ama görsel olarak baskın DEĞİL (owner
                  isteği: yığılan aksayan analizler artık kapatılabilir olmalı). */}
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={(e) => {
                    linkTrigger.capture(e.currentTarget);
                    setLinking(entry.item);
                  }}
                >
                  {t('documents.stranded.cta')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    discardTrigger.capture(e.currentTarget);
                    setDiscarding(entry.item);
                  }}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                  {t('analysis.discard.action')}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </Card>
      {linking ? (
        <StrandedLinkModal
          open={linking !== null}
          analysisUuid={linking.uuid}
          companyId={companyId}
          target={scope}
          // AYNI küme, ikinci kullanım: yukarıda "hâlâ aksıyor mu" türetimi, burada "hangi
          // kaynak HÂLÂ bağlanmayı bekliyor" süzgeci — bağlanmışı ikinci kez sunmak yinelenmiş
          // link satırı üretirdi (güvenlik incelemesi F3).
          linkedDocumentUuids={linkedDocumentUuids}
          onOpenChange={(open) => {
            if (!open) {
              closeLinking();
            }
          }}
          // Bağlama başarılıysa şerit çoğu zaman söner (kaynaklar artık `linkedDocumentUuids`
          // içinde) — o durumda tetikleyici de yoktur ve `restore()` sessizce hiçbir şey yapmaz.
          // Geriye HÂLÂ aksayan bir analiz kalırsa düğme yerinde durur ve odak oraya döner.
          onLinked={closeLinking}
        />
      ) : null}
      {/* `DocumentAnalysisResultPage.tsx`'in `Analizi sil` diyaloğuyla AYNI kablolama —
          `cancelLabel` AÇIKÇA verilir, aksi hâlde `ConfirmDialog`'un varsayılanı
          (`common.cancel` = "Vazgeç") bir silme düğmesinin yanında İKİNCİ bir "Vazgeç" gibi
          okunur (bkz. o dosyanın AYNI notu). Hata `ConfirmDialog`'un kendi `Alert`iyle
          gösterilir — bu app'in toast yüzeyi YOK ve diyalog zaten TEK bir satıra (o an
          `discarding` olan analize) bağlıdır, dolayısıyla hata her zaman doğru satıra atfedilir. */}
      <ConfirmDialog
        open={discarding !== null}
        onOpenChange={(open) => {
          if (!open) {
            closeDiscarding();
          }
        }}
        title={t('analysis.discard.confirmTitle')}
        body={t('analysis.discard.confirmBody')}
        confirmLabel={t('analysis.discard.action')}
        cancelLabel={t('upload.discard.cancel')}
        confirmVariant="danger"
        loading={discardMutation.isPending}
        error={discardMutation.isError ? discardMutation.error : undefined}
        errorFallback="analysis.discard.failed"
        onConfirm={() => discarding && discardMutation.mutate(discarding.uuid)}
      />
    </>
  );
}
